import { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import Pagination from '../../../components/common/Pagination';
import { db } from '../../../firebase/config';
import { formatUserName } from '../../../utils/userName';
import { collection, getDocs, doc, getDoc, query, where } from 'firebase/firestore';

const STATUS_META = {
  active: { label: '유효', color: '#22C55E' },
  expired: { label: '만료', color: '#9CA3AF' },
  pending: { label: '대기', color: '#F59E0B' },
};

const COMM_STATUS_ORDER = { active: 0, pending: 1, expired: 2 };

const PER_PAGE = 20;

const RealtorReferral = () => {
  const [tab, setTab] = useState('history');
  const [commissions, setCommissions] = useState([]); // type=referral
  const [relations, setRelations] = useState([]); // referrer-referee pairs
  const [loading, setLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [relPage, setRelPage] = useState(1);
  const [commSortKey, setCommSortKey] = useState('createdAt');
  const [commSortDir, setCommSortDir] = useState('desc');
  const [relSortKey, setRelSortKey] = useState('createdAt');
  const [relSortDir, setRelSortDir] = useState('desc');

  const fetchData = async () => {
    setLoading(true);
    try {
      // 추천수수료만 조회 (복합 인덱스 회피 위해 클라이언트 정렬)
      const commQ = query(
        collection(db, 'commissions'),
        where('type', '==', 'referral'),
      );
      const commSnap = await getDocs(commQ);
      const userCache = new Map();
      const loadUser = async (id) => {
        if (!id) return '-';
        if (userCache.has(id)) return userCache.get(id);
        try {
          const [s, rDoc] = await Promise.all([
            getDoc(doc(db, 'users', id)),
            getDoc(doc(db, 'realtors', id)),
          ]);
          if (s.exists()) {
            const u = s.data();
            const r = rDoc.exists() ? rDoc.data() : {};
            const name = formatUserName({
              ...u,
              business_name: r.businessName || r.business_name || u.business_name || '',
            });
            userCache.set(id, name);
            return name;
          }
        } catch { /* skip */ }
        return '-';
      };

      const comms = await Promise.all(commSnap.docs.map(async (d) => {
        const c = d.data();
        const createdAt = c.created_at?.toDate ? c.created_at.toDate() : null;
        const validUntil = createdAt ? new Date(createdAt.getFullYear() + 1, createdAt.getMonth(), createdAt.getDate()) : null;
        const now = new Date();
        const status = c.is_settled ? 'active' : (validUntil && validUntil < now ? 'expired' : 'active');
        return {
          id: d.id,
          referrerName: await loadUser(c.realtor_id),
          refereeName: await loadUser(c.referred_realtor_id),
          amount: c.amount || 0,
          status,
          createdAt: (() => {
            if (!createdAt) return '';
            const pad = (n) => String(n).padStart(2, '0');
            return `${createdAt.getFullYear()}-${pad(createdAt.getMonth() + 1)}-${pad(createdAt.getDate())} ${pad(createdAt.getHours())}:${pad(createdAt.getMinutes())}`;
          })(),
          validUntil: validUntil?.toISOString().slice(0, 10) || '',
        };
      }));
      comms.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
      setCommissions(comms);

      // 관계 현황: users 중 referrer_id 가 있는 경우
      const usersSnap = await getDocs(collection(db, 'users'));
      const rels = [];
      for (const uSnap of usersSnap.docs) {
        const u = uSnap.data();
        if (u.referrer_id) {
          rels.push({
            id: uSnap.id,
            referrerName: await loadUser(u.referrer_id),
            refereeName: await loadUser(uSnap.id),
            createdAt: u.created_at?.toDate?.()?.toISOString().slice(0, 10) || '',
          });
        }
      }
      setRelations(rels);
    } catch (err) {
      console.error('추천 로딩 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const stats = useMemo(() => {
    const now = new Date();
    const sixMonthsAgo = new Date(now); sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const thisWeekStart = new Date(now); thisWeekStart.setDate(now.getDate() - now.getDay());

    const inRange = (dateStr, from) => dateStr && new Date(dateStr) >= from;

    return {
      total: commissions.reduce((s, c) => s + c.amount, 0),
      sixMonth: commissions.filter((c) => inRange(c.createdAt, sixMonthsAgo)).reduce((s, c) => s + c.amount, 0),
      thisMonth: commissions.filter((c) => inRange(c.createdAt, thisMonthStart)).reduce((s, c) => s + c.amount, 0),
      thisWeek: commissions.filter((c) => inRange(c.createdAt, thisWeekStart)).reduce((s, c) => s + c.amount, 0),
      totalCount: commissions.length,
      cumulativeUsers: relations.length,
    };
  }, [commissions, relations]);

  const filteredComm = useMemo(() => {
    const list = commissions.filter((c) => statusFilter === 'all' || c.status === statusFilter);
    const getVal = (c) => {
      if (commSortKey === 'referrerName') return (c.referrerName || '').toLowerCase();
      if (commSortKey === 'refereeName') return (c.refereeName || '').toLowerCase();
      if (commSortKey === 'amount') return c.amount || 0;
      if (commSortKey === 'status') return COMM_STATUS_ORDER[c.status] ?? 99;
      return c.createdAt || '';
    };
    return [...list].sort((a, b) => {
      const va = getVal(a); const vb = getVal(b);
      if (va < vb) return commSortDir === 'asc' ? -1 : 1;
      if (va > vb) return commSortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [commissions, statusFilter, commSortKey, commSortDir]);
  const pagedComm = filteredComm.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const totalPages = Math.max(1, Math.ceil(filteredComm.length / PER_PAGE));

  const sortedRel = useMemo(() => {
    const getVal = (r) => {
      if (relSortKey === 'referrerName') return (r.referrerName || '').toLowerCase();
      if (relSortKey === 'refereeName') return (r.refereeName || '').toLowerCase();
      return r.createdAt || '';
    };
    return [...relations].sort((a, b) => {
      const va = getVal(a); const vb = getVal(b);
      if (va < vb) return relSortDir === 'asc' ? -1 : 1;
      if (va > vb) return relSortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [relations, relSortKey, relSortDir]);
  const pagedRel = sortedRel.slice((relPage - 1) * PER_PAGE, relPage * PER_PAGE);
  const totalRelPages = Math.max(1, Math.ceil(sortedRel.length / PER_PAGE));

  const toggleCommSort = (key) => {
    if (commSortKey === key) setCommSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setCommSortKey(key); setCommSortDir(key === 'createdAt' || key === 'amount' ? 'desc' : 'asc'); }
    setPage(1);
  };
  const toggleRelSort = (key) => {
    if (relSortKey === key) setRelSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setRelSortKey(key); setRelSortDir(key === 'createdAt' ? 'desc' : 'asc'); }
    setRelPage(1);
  };
  const commSortInd = (key) => (
    <SortInd $active={commSortKey === key}>{commSortKey === key ? (commSortDir === 'asc' ? '▲' : '▼') : '▲▼'}</SortInd>
  );
  const relSortInd = (key) => (
    <SortInd $active={relSortKey === key}>{relSortKey === key ? (relSortDir === 'asc' ? '▲' : '▼') : '▲▼'}</SortInd>
  );

  const handleExport = () => {
    const rows = [
      ['추천인', '피추천인', '금액', '상태', '추천일', '유효기간'],
      ...filteredComm.map((c) => [c.referrerName, c.refereeName, c.amount, STATUS_META[c.status]?.label || '', c.createdAt, c.validUntil]),
    ];
    const csv = rows.map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `realtor_referral_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const StatusText = ({ status }) => {
    const s = STATUS_META[status];
    if (!s) return <span>-</span>;
    return <span style={{ color: s.color, fontWeight: 500 }}>{s.label}</span>;
  };

  return (
    <Container>
      <TopRow>
        <Title>추천 수수료 관리</Title>
        <TopActions>
          <TopBtn onClick={fetchData}>새로고침</TopBtn>
        </TopActions>
      </TopRow>
      <SubHead>공인중개사 추천 수수료 적립 현황 (1명 단위로 가입되는 {stats.cumulativeUsers}건)</SubHead>

      <PolicyBox>
        추천인이 피추천인을 초대한 후 피추천인의 매출이 발생하면 5%의 추천 수수료가 추천인에게 지급됩니다.
        추천일로부터 1년간 유효하며, 만료 이후에는 수수료가 추가 지급되지 않습니다.
        지급된 수수료는 별도 정산 절차 없이 출금 가능 수익으로 자동 반영됩니다.
      </PolicyBox>

      <StatsRow>
        <StatCard><StatLabel>총 추천 수익</StatLabel><StatValue>{stats.total.toLocaleString()}원</StatValue></StatCard>
        <StatCard><StatLabel>최근 6개월</StatLabel><StatValue $color="#4A6CF7">{stats.sixMonth.toLocaleString()}원</StatValue></StatCard>
        <StatCard><StatLabel>이번 달</StatLabel><StatValue $color="#22C55E">{stats.thisMonth.toLocaleString()}원</StatValue></StatCard>
        <StatCard><StatLabel>이번 주</StatLabel><StatValue $color="#F59E0B">{stats.thisWeek.toLocaleString()}원</StatValue></StatCard>
        <StatCard><StatLabel>총 추천 건수</StatLabel><StatValue>{stats.totalCount}건</StatValue></StatCard>
        <StatCard><StatLabel>전체 누적 가입</StatLabel><StatValue>{stats.cumulativeUsers}건</StatValue></StatCard>
      </StatsRow>

      <Tabs>
        <TabBtn $active={tab === 'history'} onClick={() => setTab('history')}>추천 수수료 내역</TabBtn>
        <TabBtn $active={tab === 'relations'} onClick={() => setTab('relations')}>추천 관계 현황</TabBtn>
      </Tabs>

      {tab === 'history' && (
        <>
          <ToolRow>
            <Select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="all">상태 전체</option>
              {Object.entries(STATUS_META).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </Select>
            <ToolRight>
              <TopBtn onClick={handleExport}>CSV 내보내기</TopBtn>
            </ToolRight>
          </ToolRow>

          {loading ? (
            <EmptyText>로딩 중...</EmptyText>
          ) : pagedComm.length === 0 ? (
            <EmptyText>적립된 추천 수수료가 없습니다.</EmptyText>
          ) : (
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <Th $sortable $active={commSortKey === 'referrerName'} onClick={() => toggleCommSort('referrerName')}>추천인 (부동산){commSortInd('referrerName')}</Th>
                    <Th $sortable $active={commSortKey === 'refereeName'} onClick={() => toggleCommSort('refereeName')}>피추천인 (부동산){commSortInd('refereeName')}</Th>
                    <Th $sortable $active={commSortKey === 'amount'} onClick={() => toggleCommSort('amount')} style={{ textAlign: 'right' }}>발생 수수료{commSortInd('amount')}</Th>
                    <Th $sortable $active={commSortKey === 'createdAt'} onClick={() => toggleCommSort('createdAt')}>추천일{commSortInd('createdAt')}</Th>
                    <Th>유효기간</Th>
                    <Th $sortable $active={commSortKey === 'status'} onClick={() => toggleCommSort('status')}>상태{commSortInd('status')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {pagedComm.map((c) => (
                    <Tr key={c.id}>
                      <Td>{c.referrerName}</Td>
                      <Td>{c.refereeName}</Td>
                      <Td style={{ textAlign: 'right', fontWeight: 600 }}>{c.amount.toLocaleString()}원</Td>
                      <Td>{c.createdAt || '-'}</Td>
                      <Td>{c.validUntil || '-'}</Td>
                      <Td><StatusText status={c.status} /></Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>
          )}

          <PageFoot>
            <PageInfo>페이지 {page} / {totalPages} · 총 {filteredComm.length}건</PageInfo>
            <Pagination current={page} total={totalPages} onChange={setPage} />
          </PageFoot>
        </>
      )}

      {tab === 'relations' && (
        <>
          {loading ? (
            <EmptyText>로딩 중...</EmptyText>
          ) : pagedRel.length === 0 ? (
            <EmptyText>추천 관계가 없습니다.</EmptyText>
          ) : (
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <Th $sortable $active={relSortKey === 'referrerName'} onClick={() => toggleRelSort('referrerName')}>추천인 (부동산){relSortInd('referrerName')}</Th>
                    <Th $sortable $active={relSortKey === 'refereeName'} onClick={() => toggleRelSort('refereeName')}>피추천인 (부동산){relSortInd('refereeName')}</Th>
                    <Th $sortable $active={relSortKey === 'createdAt'} onClick={() => toggleRelSort('createdAt')}>가입일{relSortInd('createdAt')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {pagedRel.map((r) => (
                    <Tr key={r.id}>
                      <Td>{r.referrerName}</Td>
                      <Td>{r.refereeName}</Td>
                      <Td>{r.createdAt || '-'}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>
          )}

          <PageFoot>
            <PageInfo>페이지 {relPage} / {totalRelPages} · 총 {relations.length}건</PageInfo>
            <Pagination current={relPage} total={totalRelPages} onChange={setRelPage} />
          </PageFoot>
        </>
      )}
    </Container>
  );
};

export default RealtorReferral;

// --- Styled ---
const Container = styled.div``;
const TopRow = styled.div`display:flex;align-items:center;justify-content:space-between;margin-bottom:6px;`;
const Title = styled.h1`font-size:18px;font-weight:600;margin:0;`;
const TopActions = styled.div`display:flex;gap:6px;`;
const TopBtn = styled.button`padding:7px 12px;border:1px solid ${({theme})=>theme.colors.border};background:#fff;border-radius:${({theme})=>theme.radius.sm};font-size:12px;cursor:pointer;&:hover{background:#F9FAFB;}`;
const SubHead = styled.p`font-size:13px;color:${({theme})=>theme.colors.textSecondary};margin:0 0 14px;`;

const PolicyBox = styled.div`
  background:#FFFBEB;
  color:#713F12;
  border:1px solid #FDE68A;
  border-radius:${({theme})=>theme.radius.sm};
  padding:12px 16px;
  font-size:13px;
  line-height:1.6;
  margin-bottom:16px;
`;

const StatsRow = styled.div`display:grid;grid-template-columns:repeat(6,1fr);gap:10px;margin-bottom:18px;
  @media (max-width: ${({theme})=>theme.breakpoints.mobile}) { grid-template-columns:repeat(3,1fr); }
`;
const StatCard = styled.div`background:#fff;padding:14px 16px;border-radius:${({theme})=>theme.radius.md};box-shadow:${({theme})=>theme.shadow.sm};`;
const StatLabel = styled.div`font-size:11px;color:${({theme})=>theme.colors.textSecondary};margin-bottom:6px;`;
const StatValue = styled.div`font-size:16px;font-weight:700;color:${({$color})=>$color||'#1F2937'};`;

const Tabs = styled.div`display:flex;gap:8px;border-bottom:1px solid ${({theme})=>theme.colors.border};margin-bottom:16px;`;
const TabBtn = styled.button`
  padding:10px 20px;border:none;background:none;
  font-size:13px;font-weight:${({$active})=>$active?600:400};
  color:${({$active,theme})=>$active?theme.colors.primary:theme.colors.textSecondary};
  border-bottom:2px solid ${({$active,theme})=>$active?theme.colors.primary:'transparent'};
  margin-bottom:-1px;cursor:pointer;
`;

const ToolRow = styled.div`display:flex;gap:8px;align-items:center;margin-bottom:12px;`;
const ToolRight = styled.div`margin-left:auto;`;
const Select = styled.select`padding:8px 12px;border:1px solid ${({theme})=>theme.colors.border};border-radius:${({theme})=>theme.radius.sm};font-size:13px;background:#fff;&:focus{outline:none;border-color:${({theme})=>theme.colors.primary};}`;

const TableWrapper = styled.div`overflow-x:auto;background:#fff;border-radius:${({theme})=>theme.radius.md};box-shadow:${({theme})=>theme.shadow.sm};`;
const Table = styled.table`width:100%;border-collapse:collapse;`;
const Th = styled.th`
  background:#F9FAFB;
  padding:12px 14px;
  text-align:left;
  font-size:12px;
  font-weight:500;
  color:${({theme,$active})=>$active?theme.colors.primary:theme.colors.textSecondary};
  border-bottom:1px solid ${({theme})=>theme.colors.border};
  white-space:nowrap;
  ${({$sortable,theme})=>$sortable&&`
    cursor:pointer;
    user-select:none;
    &:hover { color: ${theme.colors.primary}; }
  `}
`;
const SortInd = styled.span`
  display:inline-block;
  margin-left:6px;
  color:${({theme,$active})=>$active?theme.colors.primary:'#C0C7D1'};
  font-size:10px;
  line-height:1;
  vertical-align:middle;
`;
const Tr = styled.tr`&:not(:last-child) td{border-bottom:1px solid ${({theme})=>theme.colors.borderLight};}`;
const Td = styled.td`padding:12px 14px;font-size:13px;color:${({theme})=>theme.colors.text};white-space:nowrap;`;

const EmptyText = styled.div`text-align:center;padding:56px 0;font-size:14px;color:${({theme})=>theme.colors.textSecondary};background:#fff;border-radius:${({theme})=>theme.radius.md};`;

const PageFoot = styled.div`display:flex;align-items:center;justify-content:space-between;`;
const PageInfo = styled.div`font-size:12px;color:${({theme})=>theme.colors.textSecondary};`;
